import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  FileText, 
  Copy, 
  Check, 
  Printer, 
  Code, 
  Eye, 
  X, 
  Sparkles, 
  Save, 
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import type { ArtifactPanelProps } from '../../../types/emma-artifact.types';
import { buildApiUrl } from '../../../lib/apiBase';

export const ArtifactPanel: React.FC<ArtifactPanelProps> = ({
  artifact,
  isOpen,
  onClose,
  onVersionSelect,
  onContentSave
}) => {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [selectedVersion, setSelectedVersion] = useState(1);

  useEffect(() => {
    if (artifact) {
      setEditedContent(artifact.content || '');
      setSelectedVersion(artifact.version || 1);
    }
  }, [artifact]);

  if (!isOpen || !artifact) return null;

  const handleOpenNewTab = () => {
    if (!artifact) return;
    const meta = artifact.uiAction || {};
    const previewUrl = artifact.previewUrl || meta.preview_url || meta.previewUrl || (artifact.slug ? `/api/templates/preview/${artifact.slug}` : null);
    if (previewUrl) {
      const fullUrl = previewUrl.startsWith('/api/') ? buildApiUrl(previewUrl) : previewUrl;
      window.open(fullUrl, '_blank');
      return;
    }
    const contentToOpen = editedContent || artifact.content || '';
    if (contentToOpen && contentToOpen.includes('<')) {
      const blob = new Blob([contentToOpen], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editedContent || artifact.content || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy artifact content:', err);
    }
  };

  const handleSave = () => {
    if (onContentSave) {
      onContentSave(editedContent);
    }
    setIsEditing(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const cleanContent = (rawText: string) => {
    if (!rawText) return '';
    let str = rawText.trim();
    // Strip outer triple-backtick code fences if the entire content is wrapped in one
    const match = str.match(/^```(?:html|markdown|md|xml|json)?\s*\n?([\s\S]*?)\n?```$/i);
    if (match) {
      str = match[1].trim();
    }
    // If str contains HTML markup, strip leading markdown titles like --- [ Solution ] ---
    if (str.includes('<!DOCTYPE html>') || str.includes('<html') || str.includes('<div') || str.includes('<section') || str.includes('<article')) {
      const htmlStart = str.search(/(?:<!DOCTYPE html>|<html|<div|<section|<article)/i);
      if (htmlStart > 0) {
        str = str.substring(htmlStart).trim();
      }
    }
    return str;
  };

  const rawContent = editedContent || artifact.content || '';
  const parsedContent = cleanContent(rawContent);

  const isHtml = parsedContent.startsWith('<') ||
                 parsedContent.includes('<!DOCTYPE html>') ||
                 (parsedContent.includes('<html') && parsedContent.includes('</html>')) ||
                 parsedContent.includes('<style>') ||
                 parsedContent.includes('<div');

  const contentTypeLabel = (artifact.contentType || 'artifact')
    .replace('_', ' ')
    .toUpperCase();

  const getIframeSrcDoc = (content: string) => {
    if (!content) return '';
    let doc = content.trim();

    const isHtml = /^\s*</.test(doc) ||
                   doc.toLowerCase().includes('<!doctype html>') ||
                   doc.toLowerCase().includes('<html') ||
                   doc.toLowerCase().includes('<div') ||
                   doc.toLowerCase().includes('<style') ||
                   doc.toLowerCase().includes('<section') ||
                   doc.toLowerCase().includes('<article') ||
                   doc.toLowerCase().includes('<header') ||
                   doc.toLowerCase().includes('<main') ||
                   doc.toLowerCase().includes('<table') ||
                   doc.toLowerCase().includes('<svg');

    const hasTailwind = doc.includes('cdn.tailwindcss.com');
    const tailwindScript = hasTailwind ? '' : '<script src="https://cdn.tailwindcss.com"></script>';
    const fontLink = '<link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">';

    if (isHtml) {
      if (doc.toLowerCase().includes('<html') || doc.toLowerCase().includes('<!doctype html>')) {
        const htmlIdx = doc.search(/(?:<!DOCTYPE html>|<html)/i);
        if (htmlIdx > 0) {
          doc = doc.substring(htmlIdx).trim();
        }
        if (!hasTailwind && doc.toLowerCase().includes('<head>')) {
          doc = doc.replace(/<head>/i, `<head>${tailwindScript}${fontLink}`);
        } else if (!hasTailwind) {
          doc = doc.replace(/<html[^>]*>/i, `$&<head>${tailwindScript}${fontLink}</head>`);
        }
        return doc;
      }

      return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    ${tailwindScript}
    ${fontLink}
    <style>
      *, ::before, ::after { box-sizing: border-box; }
      html, body {
        margin: 0;
        padding: 0;
        min-height: 100vh;
        background: #0f172a;
        color: #f8fafc;
        font-family: 'Outfit', 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      }
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        padding: 1.5rem 1rem;
      }
      .citation-badge {
        background-color: #3b82f6 !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 4px !important;
        padding: 2px 6px !important;
        font-size: 10px !important;
        font-weight: 700 !important;
        margin-left: 4px !important;
        cursor: pointer !important;
        vertical-align: super !important;
        line-height: 1 !important;
      }
    </style>
  </head>
  <body>
    ${doc}
  </body>
</html>`;
    }

    const formattedBody = doc
      .split('\n\n')
      .map((paragraph) => {
        const p = paragraph.trim();
        if (!p) return '';
        if (/^\d+%/i.test(p) || /^\$\d+/i.test(p)) {
          const parts = p.split('\n');
          const stat = parts[0];
          const rest = parts.slice(1).join('<br/>');
          return `<div class="stat-highlight" style="background: linear-gradient(135deg, #064e3b 0%, #047857 100%); border: 1px solid #059669; border-radius: 16px; padding: 24px; margin: 24px 0; box-shadow: 0 10px 25px rgba(4, 120, 87, 0.2);">
            <div style="font-size: 42px; font-weight: 900; color: #34d399; line-height: 1.1;">${stat}</div>
            ${rest ? `<div style="font-size: 14px; color: #e2e8f0; font-weight: 500; margin-top: 8px;">${rest}</div>` : ''}
          </div>`;
        }
        if (p.startsWith('# ')) {
          return `<h1 style="font-size: 28px; font-weight: 800; color: #ffffff; margin-top: 24px; margin-bottom: 12px; border-bottom: 2px solid #059669; padding-bottom: 8px;">${p.replace('# ', '')}</h1>`;
        }
        if (p.startsWith('## ')) {
          return `<h2 style="font-size: 22px; font-weight: 700; color: #34d399; margin-top: 20px; margin-bottom: 10px;">${p.replace('## ', '')}</h2>`;
        }
        if (p.startsWith('### ')) {
          return `<h3 style="font-size: 18px; font-weight: 600; color: #60a5fa; margin-top: 16px; margin-bottom: 8px;">${p.replace('### ', '')}</h3>`;
        }
        const formattedP = p.replace(/\[SRC-(\d+)\]/g, '<span style="background: #2563eb; color: #ffffff; font-size: 11px; font-weight: 700; padding: 2px 6px; border-radius: 4px; margin-left: 4px; display: inline-block;">SRC-$1</span>');
        return `<p style="font-size: 15px; line-height: 1.7; color: #e2e8f0; margin-bottom: 16px;">${formattedP.replace(/\n/g, '<br/>')}</p>`;
      })
      .join('\n');

    return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    ${tailwindScript}
    ${fontLink}
    <style>
      html, body {
        margin: 0;
        padding: 32px 20px;
        background: #0f172a;
        color: #f8fafc;
        font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-sizing: border-box;
      }
      * { box-sizing: border-box; }
      .card-canvas {
        max-width: 780px;
        margin: 0 auto;
        background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
        border: 1px solid #334155;
        border-radius: 24px;
        padding: 40px;
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
      }
    </style>
  </head>
  <body>
    <div class="card-canvas">
      ${formattedBody}
    </div>
  </body>
</html>`;
  };

  return (
    <div className="emma-artifact-panel flex flex-col h-full bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
      {/* Header Bar */}
      <div className="emma-artifact-header flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Sparkles size={18} />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
              {artifact.title || 'Generated Artifact'}
            </h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                {contentTypeLabel}
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                v{selectedVersion}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Version Selector */}
          {artifact.version > 1 && (
            <div className="relative inline-block text-left">
              <select
                value={selectedVersion}
                onChange={(e) => {
                  const ver = Number(e.target.value);
                  setSelectedVersion(ver);
                  if (onVersionSelect) onVersionSelect(ver);
                }}
                className="appearance-none bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 py-1 pl-2.5 pr-6 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
              >
                {Array.from({ length: artifact.version }, (_, i) => i + 1).map((v) => (
                  <option key={v} value={v}>
                    v{v}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} className="absolute right-2 top-2.5 text-slate-400 pointer-events-none" />
            </div>
          )}

          {/* Open in New Tab Button */}
          <button
            type="button"
            onClick={handleOpenNewTab}
            className="p-1.5 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-200/50 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Open in New Tab"
          >
            <ExternalLink size={16} />
          </button>

          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Close Panel"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="emma-artifact-body flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50/50 dark:bg-slate-900/50">
        {isEditing ? (
          <div className="flex flex-col h-full bg-slate-950 border border-slate-800 rounded-xl shadow-2xl overflow-hidden">
            {/* Editor Window Header Bar */}
            <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-b border-slate-800 text-xs font-mono text-slate-400">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80 inline-block" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80 inline-block" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80 inline-block" />
                <span className="ml-2 font-medium text-slate-300">
                  {isHtml ? 'document_source.html' : 'document_source.md'}
                </span>
              </div>
              <div className="flex items-center gap-4 text-[11px] text-slate-500">
                <span>Lines: {(editedContent || '').split('\n').length}</span>
                <span>Chars: {(editedContent || '').length}</span>
                <span className="text-emerald-400 font-semibold uppercase">{isHtml ? 'HTML / CSS' : 'MARKDOWN'}</span>
              </div>
            </div>

            {/* Code Textarea Editor */}
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full flex-1 p-4 font-mono text-xs leading-relaxed text-emerald-300 dark:text-emerald-300 bg-slate-950 border-none focus:outline-none resize-none tracking-wide selection:bg-emerald-900 selection:text-white"
              placeholder="Edit artifact markup..."
              spellCheck={false}
            />

            {/* Editor Toolbar Footer */}
            <div className="flex items-center justify-between px-4 py-3 bg-slate-900 border-t border-slate-800">
              <span className="text-[11px] text-slate-500 font-mono">
                Press Save Changes to update live preview
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditedContent(artifact.content || '');
                    setIsEditing(false);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-500 rounded-md shadow-md transition-colors"
                >
                  <Save size={14} />
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="artifact-preview-area w-full h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden flex flex-col min-h-0">
            {(() => {
              const meta = artifact.uiAction || {};
              const activePreviewUrl = artifact.previewUrl || meta.preview_url || meta.previewUrl || (artifact.slug ? `/api/templates/preview/${artifact.slug}` : null);
              const rawPreviewUrl = activePreviewUrl ? (activePreviewUrl.startsWith('/api/') ? buildApiUrl(activePreviewUrl) : activePreviewUrl) : null;
              const cacheBuster = artifact.version || (artifact as any).updatedAt || Date.now();
              const fullPreviewUrl = rawPreviewUrl ? `${rawPreviewUrl}${rawPreviewUrl.includes('?') ? '&' : '?'}v=${cacheBuster}` : null;

              if (fullPreviewUrl) {
                return (
                  <iframe
                    key={fullPreviewUrl}
                    src={fullPreviewUrl}
                    className="w-full h-full flex-1 border-0 bg-white"
                    title="Artifact Document Preview"
                  />
                );
              }

              if (parsedContent) {
                return (
                  <iframe
                    srcDoc={getIframeSrcDoc(parsedContent)}
                    className="w-full h-full flex-1 border-0 bg-slate-900"
                    title="Artifact Document Preview"
                  />
                );
              }

              return (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center bg-slate-900 text-slate-300">
                  <div className="w-10 h-10 rounded-full border-2 border-emerald-500/30 border-t-emerald-500 animate-spin mb-4" />
                  <h3 className="text-sm font-semibold text-slate-100 mb-1">Generating Visual Asset...</h3>
                  <p className="text-xs text-slate-400 max-w-xs">
                    Emma is building your grounded poster design. Content will render here automatically upon completion.
                  </p>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Action Toolbar */}
      <div className="emma-artifact-toolbar flex items-center justify-between px-4 py-2.5 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-800 text-xs">
        <div className="flex items-center gap-2">
          {/* Toggle Edit Source */}
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 font-medium rounded-md transition-colors ${
              isEditing 
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' 
                : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800'
            }`}
          >
            {isEditing ? <Eye size={14} /> : <Code size={14} />}
            <span>{isEditing ? 'Preview Mode' : 'Edit Source'}</span>
          </button>

          {/* Open in New Tab Toolbar Button */}
          <button
            type="button"
            onClick={handleOpenNewTab}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-950/40 rounded-md transition-colors"
            title="Open preview in new browser tab"
          >
            <ExternalLink size={14} />
            <span>Open in New Tab</span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Copy Button */}
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-md transition-colors"
            title="Copy Raw Content"
          >
            {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
            <span>{copied ? 'Copied!' : 'Copy'}</span>
          </button>

          {/* Export PDF / Print */}
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-md transition-colors"
            title="Print or Save as PDF"
          >
            <Printer size={14} />
            <span>Export PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtifactPanel;
